// frontend/src/components/chatbot/Chatbot.js
import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  Box,
  Button,
  Input,
  Text,
  VStack,
  HStack,
  IconButton,
  Flex,
  Image,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from "@chakra-ui/react";
import { FaRobot, FaTimes } from "react-icons/fa";
import axios from "axios";
import testupscale from "../../assets/images/testupscale.png";
import dentacasai from "../../assets/icons/dentacasaai.svg";
import { useLocation } from "react-router-dom"; // ✅ Import useLocation
import { motion } from "framer-motion";
import {
  backgroundLight,
  color8,
  newBorder,
  primary,
  primaryAlt,
  textPrimary,
  textSecondary,
  white,
} from "../../utils/colors";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import orb from "../../assets/lottie/ai-orb.lottie";
import audioLines from "../../assets/icons/audio-lines.svg";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

const ChatBot = ({ onOpenPatient }) => {
  const { t } = useTranslation();
  const location = useLocation(); // ✅ Use useLocation

  const [isTyping, setIsTyping] = useState(false);

  const lastResultsRef = useRef([]); // stores last toolPayload
  const [awaitingChoice, setAwaitingChoice] = useState(false);
  const [awaitingConfirm, setAwaitingConfirm] = useState(null); // store single patient cand if waiting for "yes"

  // Convert "treatment-plans" → "treatmentPlans"
  const normalizePageKey = (path) =>
    path
      .replace(/-([a-z])/g, (_, letter) => letter.toUpperCase()) // Convert -x to X
      .replace(/^\w/, (c) => c.toLowerCase()); // Ensure first letter is lowercase

  const page = normalizePageKey(location.pathname.split("/").pop());

  const tenantDetailsBySlug = useSelector((state) => state.tenantDetailsBySlug);
  const { tenant } = tenantDetailsBySlug;

  // console.log("Normalized page:", page);

  // Dynamic text for TooltipTwo based on the normalized page
  const tooltipTwoText =
    {
      dashboard: t("tooltip.dashboardTip"),
      patients: t("tooltip.patientsTip"),
      treatmentPlans: t("tooltip.treatmentPlansTip"),
      employees: t("tooltip.employeesTip"),
      orders: t("tooltip.suppliesTip"),
      services: t("tooltip.servicesTip"),
      clinicSettings: t("tooltip.clinicSettingsTip"),
    }[page] || t("tooltip.defaultTip");

  // Check localStorage to see if tooltips were closed before
  useEffect(() => {
    const tooltipState =
      JSON.parse(localStorage.getItem("chatbot_tooltips")) || {};

    if (!tooltipState.tooltipOneClosed) {
      setTimeout(() => setIsTooltipOneShown(true), 1500); // Show Tooltip One after 3 sec
    }
    if (!tooltipState.tooltipTwoClosed) {
      setTimeout(() => setIsTooltipTwoShown(true), 2000); // Show Tooltip Two after 4 sec
    }
  }, []);

  // STATE
  const [message, setMessage] = useState("");
  const [reply, setReply] = useState("");
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  // Replace your `isOpen` toggle logic with:
  const [isOpen, setIsOpen] = useState(false);

  const [chatHistory, setChatHistory] = useState([]);

  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTooltipOneShown, setIsTooltipOneShown] = useState(false);
  const [isTooltipTwoShown, setIsTooltipTwoShown] = useState(false);

  // REF
  const chatEndRef = useRef(null);

  // LOTTIE
  const [dotLottie, setDotLottie] = useState(null);

  const dotLottieRefCallback = (instance) => {
    setDotLottie(instance);
  };

  // Trigger open when user focuses or types
  const handlePromptFocus = () => {
    if (!isOpen) setIsOpen(true);
  };

  const tryHandleOpenCommand = (msg) => {
    const results = lastResultsRef.current || [];
    if (!results.length) return false;

    const m = msg.toLowerCase().trim();

    // If we had a single candidate and user says "open" / "yes"
    if (awaitingConfirm && /^(open|yes|ok|sure|go|open it)$/i.test(m)) {
      const p = awaitingConfirm;
      onOpenPatient?.(buildMetaFromToolPatient(p));
      setChatHistory((prev) => [
        ...prev,
        { role: "bot", content: `Opening ${p.name}…` },
      ]);
      setAwaitingConfirm(null);
      lastResultsRef.current = [];
      setAwaitingChoice(false);
      return true;
    }

    // If multiple results, parse "open N" / "open ID" / "open name"
    if (awaitingChoice || /^open\b/.test(m)) {
      // open by index: "open 2"
      const idxMatch = m.match(/\bopen\s+(\d+)\b/);
      if (idxMatch) {
        const idx = parseInt(idxMatch[1], 10) - 1;
        const p = results[idx];
        if (p) {
          onOpenPatient?.(buildMetaFromToolPatient(p));
          setChatHistory((prev) => [
            ...prev,
            { role: "bot", content: `Opening ${p.name}…` },
          ]);
          lastResultsRef.current = [];
          setAwaitingChoice(false);
          return true;
        }
      }

      // open by patientID
      const idMatch = m.match(/\bopen\s+([A-Za-z0-9\-_.]+)\b/);
      if (idMatch) {
        const token = idMatch[1].toLowerCase();
        const p =
          results.find((r) => String(r.patientID).toLowerCase() === token) ||
          results.find((r) => String(r.id).toLowerCase() === token) ||
          results.find((r) => (r.name || "").toLowerCase().includes(token));
        if (p) {
          onOpenPatient?.(buildMetaFromToolPatient(p));
          setChatHistory((prev) => [
            ...prev,
            { role: "bot", content: `Opening ${p.name}…` },
          ]);
          lastResultsRef.current = [];
          setAwaitingChoice(false);
          return true;
        }
      }

      // Just "open" with 1 candidate? open it
      if (/^open$/.test(m) && results.length === 1) {
        const p = results[0];
        onOpenPatient?.(buildMetaFromToolPatient(p));
        setChatHistory((prev) => [
          ...prev,
          { role: "bot", content: `Opening ${p.name}…` },
        ]);
        lastResultsRef.current = [];
        setAwaitingChoice(false);
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (dotLottie) {
      dotLottie.play();
    }
  }, [dotLottie]);

  // Define page-specific suggested questions
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatHistory]); // Runs whenever chatHistory updates

  useEffect(() => {
    if (isOpen && chatHistory.length === 0) {
      const welcomeMessage = {
        role: "bot",
        content:
          "Hello! I'm Dentacasa AI, your virtual assistant. How can I help you today?",
      };
      setChatHistory([welcomeMessage]);
    }
  }, [isOpen]);

  useEffect(() => {
    const questions = {
      dashboard: [
        t("ai.howToUseDashboardOne"),
        t("ai.howToUseDashboardTwo"),
        t("ai.howToUseDashboardThree"),
      ],

      treatmentPlans: [
        t("ai.howToUseTreatmentPlansOne"),
        t("ai.howToUseTreatmentPlansTwo"),
        t("ai.howToUseTreatmentPlansThree"),
      ],
      patients: [
        t("ai.howToUsePatientsOne"),
        t("ai.howToUsePatientsTwo"),
        t("ai.howToUsePatientsThree"),
      ],
      employees: [
        t("ai.howToUseEmployeesOne"),
        t("ai.howToUseEmployeesTwo"),
        t("ai.howToUseEmployeesThree"),
      ],
      orders: [
        t("ai.howToUseOrdersOne"),
        t("ai.howToUseOrdersTwo"),
        t("ai.howToUseOrdersThree"),
      ],
      clinicSettings: [
        t("ai.howToUseClinicSettingsOne"),
        t("ai.howToUseClinicSettingsTwo"),
        t("ai.howToUseClinicSettingsThree"),
      ],
    };
    setSuggestedQuestions(questions[page] || []);
  }, [page]);

  // Function to handle tooltip close and save state
  // Function to handle tooltip close and save state
  const closeTooltip = (tooltip) => {
    const tooltipState =
      JSON.parse(localStorage.getItem("chatbot_tooltips")) || {};

    if (tooltip === "one") {
      setIsTooltipOneShown(false);
      tooltipState.tooltipOneClosed = true;
    } else if (tooltip === "two") {
      setIsTooltipTwoShown(false);
      tooltipState.tooltipTwoClosed = true;
    }

    localStorage.setItem("chatbot_tooltips", JSON.stringify(tooltipState));
  };

  // Function to send a message to the chatbot API
  // Function to send a message to the chatbot API
  const sendMessage = async (msg) => {
    if (!msg.trim()) return;

    // 🛑 If message is an "open" command for last results, handle locally
    if (tryHandleOpenCommand(msg)) {
      setMessage("");
      return;
    }

    const userMsg = { role: "user", content: msg };
    setChatHistory((prev) => [...prev, userMsg]);
    setMessage("");
    setReply("");
    setIsTyping(true);

    try {
      const response = await axios.post("/api/chatbot", {
        message: msg,
        userId: "user123",
        tenantID: tenant?.tenantID || "defaultTenantID",
      });

      const { reply, tool, toolPayload } = response.data;
      const botReply = { role: "bot", content: reply };

      if (tool === "searchPatients" && Array.isArray(toolPayload)) {
        lastResultsRef.current = toolPayload;

        if (toolPayload.length === 1) {
          // Auto-open OR ask to confirm. Pick one:
          // (A) Auto-open immediately:
          // onOpenPatient?.(buildMetaFromToolPatient(toolPayload[0]));
          // setChatHistory((prev) => [...prev, botReply, { role: "bot", content: `Opening ${toolPayload[0].name}…` }]);

          // (B) Ask to confirm:
          setChatHistory((prev) => [
            ...prev,
            botReply,
            { role: "bot", isList: true, patients: toolPayload },
            { role: "bot", content: "Found one match. Open it?" },
          ]);
          setAwaitingConfirm(toolPayload[0]);
          setAwaitingChoice(false);
        } else if (toolPayload.length > 1) {
          setChatHistory((prev) => [
            ...prev,
            botReply,
            { role: "bot", isList: true, patients: toolPayload },
            {
              role: "bot",
              content: "Which one should I open? Say “open 2” or “open 12345”.",
            },
          ]);
          setAwaitingChoice(true);
          setAwaitingConfirm(null);
        } else {
          setChatHistory((prev) => [...prev, botReply]);
          setAwaitingChoice(false);
          setAwaitingConfirm(null);
          lastResultsRef.current = [];
        }
      } else {
        setChatHistory((prev) => [...prev, botReply]);
      }
    } catch (e) {
      console.error(e);
      setChatHistory((prev) => [
        ...prev,
        { role: "bot", content: "Oops, something went wrong!" },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  const buildMetaFromToolPatient = (p) => ({
    id: p.id || p.patientID,
    name: p.name,
    email: p.email || null,
    phone: p.phone || null,
  });

  // LOG
  // console.log("chatHistory:", chatHistory);
  // console.log("suggestedQuestions:", suggestedQuestions);
  // console.log("isHovered:", isHovered);

  return (
    <>
      <Box w="100%" position="fixed" bottom="0rem" left="initial" zIndex="1000">
        <Box w="100%">
          {/* Custom Tooltip */}
          {isTooltipOneShown && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.2 }}
              style={{
                position: "absolute",
                bottom: "11rem",
                right: "0.5rem",
                background: white,
                color: textPrimary,
                padding: "1rem 1rem",
                borderRadius: "1rem",
                border: `1px solid ${newBorder}`,
                fontSize: "0.8rem",
                fontWeight: "500",
                whiteSpace: "nowrap",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
              }}
            >
              <Flex align="center" justify="space-between" mt={1}>
                <Text fontSize="sm" mr={2}>
                  {t("tooltip.tooltipOne")}
                </Text>

                <IconButton
                  bg={backgroundLight}
                  borderRadius="10rem"
                  icon={<FaTimes />}
                  size="xs"
                  onClick={() => closeTooltip("one")}
                  color={textSecondary}
                  _hover={{
                    bg: primaryAlt,
                    color: white,
                  }}
                />
              </Flex>
            </motion.div>
          )}

          {/* Custom Tooltip */}
          {isTooltipTwoShown && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              transition={{ duration: 0.2 }}
              style={{
                position: "absolute",
                bottom: "6rem",
                right: "0.5rem",
                background: primaryAlt,
                color: white,
                padding: "1rem 1rem",
                borderRadius: "1rem",
                border: `1px solid ${primary}`,
                fontSize: "0.8rem",
                fontWeight: "500",
                whiteSpace: "nowrap",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
              }}
            >
              <Flex align="center" justify="space-between" mt={1}>
                <Text fontSize="sm" mr={2}>
                  {tooltipTwoText}
                </Text>

                <IconButton
                  bg={backgroundLight}
                  borderRadius="10rem"
                  icon={<FaTimes />}
                  size="xs"
                  onClick={() => closeTooltip("two")}
                  color={textSecondary}
                  _hover={{
                    bg: primaryAlt,
                    color: white,
                  }}
                />
              </Flex>
            </motion.div>
          )}

          {/* Floating Chat Button */}

          {/* Chat Window */}
          {isOpen && (
            <Box
              as={motion.div}
              initial={{ opacity: 0, y: 200 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 200 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              m="0 auto"
              style={{
                position: "relative",
                bottom: "0rem",
                right: "0",
                width: "600px",
              }}
            >
              <Box
                w="600px"
                // bg={backgroundLight}
                backdropFilter="blur(3px)"
                bg="rgba(255, 255, 255, 0.8)"
                // bg={tint(0.9, primary)}
                p={4}
                boxShadow="0 0 12px rgba(0, 0, 0, 0.15)"
                borderRadius="1rem"
              >
                {/* Chat Header */}
                <HStack justifyContent="space-between">
                  <Flex align="center">
                    <Image src={dentacasai} h="20px" mr={2} />
                    <Text fontSize="0.875rem" fontWeight="600">
                      Dentacasa AI
                    </Text>
                  </Flex>

                  <IconButton
                    bg={white}
                    borderRadius="10rem"
                    icon={<FaTimes />}
                    size="xs"
                    onClick={() => setIsOpen(false)}
                    color={textSecondary}
                    _hover={{
                      bg: primaryAlt,
                      color: white,
                    }}
                  />
                </HStack>

                {/* Chat History */}
                <VStack
                  spacing={2}
                  mt={3}
                  // bg={white}
                  minH="300px"
                  maxH="300px"
                  p="1rem"
                  borderRadius="1rem"
                  overflowY="auto"
                  align="stretch"
                >
                  {chatHistory.map((chat, index) => (
                    <Box
                      key={index}
                      bg={chat.role === "user" ? color8 : primaryAlt}
                      color={chat.role === "user" ? textPrimary : "white"}
                      p={3}
                      fontSize="0.875rem"
                      borderRadius="0.75rem"
                      alignSelf={
                        chat.role === "user" ? "flex-end" : "flex-start"
                      }
                      maxW="80%"
                    >
                      {!chat.isList && chat.content}

                      {chat.isList && (
                        <VStack align="stretch" spacing={2}>
                          {chat.patients.map((p) => (
                            <Box
                              key={p.id || p.patientID}
                              onClick={() => {
                                if (onOpenPatient)
                                  onOpenPatient(buildMetaFromToolPatient(p));
                              }}
                              _hover={{ opacity: 0.9, cursor: "pointer" }}
                              p={2}
                              bg="rgba(255,255,255,0.12)"
                              borderRadius="0.5rem"
                            >
                              <Text fontWeight="600">{p.name || "—"}</Text>
                              <Text fontSize="xs" opacity={0.9}>
                                {p.patientID || ""}{" "}
                                {p.email ? `• ${p.email}` : ""}{" "}
                                {p.phone ? `• ${p.phone}` : ""}
                              </Text>
                              <Button
                                size="xs"
                                mt={2}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onOpenPatient)
                                    onOpenPatient(buildMetaFromToolPatient(p));
                                }}
                              >
                                Open
                              </Button>
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  ))}

                  {/* {chatHistory.map((chat, index) => (
                  <Box
                    key={index}
                    bg={chat.role === "user" ? color8 : primaryAlt}
                    color={chat.role === "user" ? textPrimary : "white"}
                    p={3}
                    fontSize="0.875rem"
                    borderRadius="0.75rem"
                    alignSelf={chat.role === "user" ? "flex-end" : "flex-start"}
                    maxW="80%"
                  >
                    {chat.content}
                  </Box>
                ))} */}

                  {isTyping && (
                    <Box
                      bg={primaryAlt}
                      color="white"
                      p={3}
                      fontSize="0.875rem"
                      borderRadius="0.75rem"
                      alignSelf="flex-start"
                      maxW="80%"
                    >
                      <motion.div
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <Flex align="center">
                          <FaRobot />
                          <Text ml={1}>{t("ai.aiTyping")}</Text>
                        </Flex>
                      </motion.div>
                    </Box>
                  )}

                  {/* 🔽 Invisible div at the end of chat */}
                  <div ref={chatEndRef} />
                </VStack>

                {/* Suggested Questions */}
                <Flex p="0.25rem 0" mt={3} direction="column">
                  {/* <Text fontSize="xs" fontWeight="500" mb={2}>
                  {t("common.suggestedQuestions")}
                </Text> */}
                  {suggestedQuestions.map((q, index) => (
                    <Text
                      as={motion.p}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        transition: { delay: index * 0.1 },
                      }}
                      whileHover={{ x: 2 }}
                      key={index}
                      fontSize="sm"
                      fontWeight="500"
                      onClick={() => sendMessage(q)}
                      w="100%"
                      p="0.25rem 0"
                      color={textSecondary}
                      _hover={{
                        fontWeight: "500",
                        cursor: "pointer",
                        color: textPrimary,
                      }}
                    >
                      {q}
                    </Text>
                  ))}
                </Flex>
              </Box>
            </Box>
          )}

          {/* Input Field */}
          <Flex
            w="100%"
            align="center"
            backdropFilter="blur(3px)"
            zIndex="1000"
            h="68px"
          >
            <Flex w="100%" maxW="40%" m="0 auto" align="center" h="68px">
              <InputGroup onClick={handlePromptFocus} h="48px" p="0">
                <InputLeftElement pointerEvents="none" h="48px">
                  <DotLottieReact
                    src={orb}
                    loop
                    autoplay
                    dotLottieRefCallback={dotLottieRefCallback}
                    style={{ width: 32, height: 32 }}
                  />
                </InputLeftElement>

                <Input
                  autoFocus
                  size="lg"
                  bg={white}
                  value={message}
                  placeholder={t("common.aiPrompt")}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      sendMessage(message);
                    }
                  }}
                  fontSize="1rem"
                  fontWeight="500"
                  color={textPrimary}
                  border={`1px solid ${newBorder}`}
                  borderRadius="10.75rem"
                  boxShadow="none !important"
                  pl="2.25rem"
                  _focus={{
                    borderColor: newBorder,
                    boxShadow: "sm",
                  }}
                />

                <InputRightElement h="100%">
                  <Flex h="1.75rem" w="1.75rem" align="center" justify="center">
                    <Image src={audioLines} h="1rem" opacity="0.2" />
                  </Flex>
                </InputRightElement>
              </InputGroup>
            </Flex>
          </Flex>
        </Box>
      </Box>
    </>
  );
};

const Video = styled.video`
  display: block;
  width: 169%;
  -o-object-fit: cover;
  object-fit: cover;
  height: 169%;
  margin-top: -1.25rem;
`;

export default ChatBot;
